import { useEffect } from 'react';
import { GUIActions } from '../types/game-types';
import { HealthBar } from './HealthBar';
import { useGameDispatch } from '../store/global-context';
import { useGameStatusStore } from '../store/zustand/useGameStatusStore';
import { useGameStatsStore } from '../store/zustand/useGameStatsStore';
import { CONFIG } from '../config';
import { IntervalController } from '../controllers/interval-controller';
import styles from './styles.module.scss';
import { minutesToMillisec } from '@game-libs/exc-libs/src/helpers';
import { useVictory } from '../store/hooks/useVictory';
import { useCheating } from '../store/hooks/useCheating';
import { FloatingNumbersSource } from './FloatingNumbersSource';
import { EventsManager } from '@game-libs/events-manager';
import { GameplayEvents } from '../types/game-events';
import { GameButton } from './GameButton';
import { useCheckAfterTick } from '../store/hooks/useCheckAfterTick';
import { useFirstManaOutDialog } from '../store/hooks/useFirstManaOutDialog';
import { GeneratorsList } from './GeneratorsList';
import { useTranslation } from 'react-i18next';
import { SettingsMenu } from '../components/SettingsMenu';
import { useHotkeys } from 'react-hotkeys-hook';
import { CRTFilter } from './CRTFilter';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { GameInputsMediator } from '@game-libs/inputs-mediator';
import { InputActions } from '@game-libs/models';
import { StatsAndInfoList } from './StatsAndInfoList';
import { TimedEvents } from './TimedEvents';
import { useHandleTimedEvents } from '../store/hooks/useHandleTimedEvents';
import { TimedEventsOrchestrator } from '@game-libs/timed-events-orchestrator';
import { openLink } from '../utils/open-links';
import { DungeonCrawler } from '../components/DungeonCrawler';
import { useUpgradesStore } from '../store/zustand/useUpgradesStore';

export const GameGUI = () => {
  const dispatch = useGameDispatch();
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const moneyBidsKilled = useGameStatsStore((s) => s.byLevel[s.activeLevelId].moneyBidsKilled);
  const tickLength = useGameStatusStore((s) => s.tickLength);
  const playedCoupDeGrace = useGameStatusStore((s) => s.playedCoupDeGrace);
  const dragonName = useGameStatusStore((s) => s.dragonName);
  const metBigDragon = useGameStatusStore((s) => s.metBigDragon);
  const won = useGameStatusStore((s) => s.won);
  const buyMax = useGameStatusStore((gameStatusState) => gameStatusState.buyMax);
  const showUI = useGameStatusStore((gameStatusState) => gameStatusState.showUI);
  const { on, emit } = EventsManager();
  const { t } = useTranslation('common');
  const { settings } = GlobalGameStore();
  useCheating();
  useCheckAfterTick();
  useFirstManaOutDialog();
  useHandleTimedEvents();
  useVictory();

  const crtFilterEnabled = settings.value.crtFilterEnabled ?? false;

  useEffect(() => {
    const { removeEvent } = TimedEventsOrchestrator();

    const timer = new IntervalController(() => {
      // TICK: Zustand/timed-events only, returns same GUIGameState (no context churn). Then generator/combat slice.
      dispatch({ type: GUIActions.TICK });
      dispatch({ type: GUIActions.GENERATE_GENERATORS });
    }, tickLength);

    if (!playedCoupDeGrace || dragonName === 'dummy') {
      timer.start();
    }

    const waitForLastHitUnsub = on(GameplayEvents.WAIT_FOR_LAST_HIT, () => {
      timer.stop();
    });

    const startEndlessUnsub = on('start_endless', () => {
      removeEvent('roar');

      timer.start();

      dispatch({ type: GUIActions.CHANGE_LEVEL, payload: { levelName: 'dummy' } });
    });

    return () => {
      startEndlessUnsub();
      waitForLastHitUnsub();
      timer.stop();
    };
    // `playedCoupDeGrace` / `dragonName` gate whether the loop runs; they are not tied to `tickLength`.
    // After a save-version mismatch, `initReducerState` resets game status — if this effect only depended on
    // `tickLength`, the first run could see stale persisted flags (timer never starts, warriors never tick).
  }, [tickLength, playedCoupDeGrace, dragonName, dispatch]);

  useEffect(() => {
    const { inputEvents, releaseEvents } = GameInputsMediator();

    const timer = new IntervalController(
      () => {
        if (!metBigDragon) {
          dispatch({ type: GUIActions.CLICK_DRAGON });
        }
      },
      tickLength / upgrades.click.featherFinger.multiplier,
      true,
    );

    const inputUnsub = inputEvents.on(InputActions.ATTACK, () => {
      if (metBigDragon) {
        return;
      }
      if (upgrades.click.carpalCure.purchased > 0) {
        timer.start();
      } else {
        timer.tryFireOnce();
      }
    });

    const releaseUnsub = releaseEvents.on(InputActions.ATTACK, () => {
      timer.stop();
    });

    const clickUnsub = on(GameplayEvents.CLICK_GAME_DRAGON, () => {
      if (upgrades.click.carpalCure.purchased > 0) {
        timer.start();
      } else {
        timer.tryFireOnce();
      }
    });

    const unclickUnsub = on(GameplayEvents.STOP_CLICK_GAME_DRAGON, () => {
      timer.stop();
    });

    const roarUnsub = on(GameplayEvents.DRAGON_ROAR, () => {
      dispatch({ type: GUIActions.ADD_STUN_EFFECT });
    });

    on(GameplayEvents.WAIT_FOR_LAST_HIT, () => {
      timer.stop();
    });

    return () => {
      inputUnsub();
      releaseUnsub();
      clickUnsub();
      unclickUnsub();
      roarUnsub();
      timer.stop();
    };
  }, [
    upgrades.click.featherFinger.multiplier,
    upgrades.click.carpalCure.purchased,
    won,
    metBigDragon,
    tickLength,
    dispatch,
  ]);

  useEffect(() => {
    return on(GameplayEvents.CLICK_GAME_BIRD, ({ screenCoordinates }) => {
      dispatch({ type: GUIActions.CLICK_BIRD, payload: { screenCoordinates } });
    });
  }, []);

  useEffect(() => {
    const birdWaitTime = Math.min(0.15 * minutesToMillisec(moneyBidsKilled ** 2 + 1), minutesToMillisec(4));

    const timer = setTimeout(() => {
      emit(GameplayEvents.CREATE_GAME_BIRD);
    }, birdWaitTime);

    return () => {
      clearTimeout(timer);
    };
  }, [moneyBidsKilled]);

  // Heal dummy dragon completely every 5 seconds
  useEffect(() => {
    if (dragonName !== 'dummy') {
      return;
    }

    const healAmount = CONFIG.game.initialDragonHealth.dummy;
    const timer = new IntervalController(() => {
      dispatch({ type: GUIActions.HEAL_DRAGON, payload: healAmount });
    }, 2.888 * tickLength);

    timer.start();

    return () => {
      timer.stop();
    };
  }, [dragonName, tickLength]);

  useHotkeys(
    'b',
    () => {
      dispatch({ type: GUIActions.TOGGLE_BUY_MAX });
    },
    [],
  );

  return (
    <div className={styles.gameGuiContainer}>
      <div className={styles.gameName}>
        <div className={styles.steamButton}>
          <a
            id="wishlist"
            className={styles.wishlistButton}
            onClick={() => {
              openLink('https://store.steampowered.com/app/3834590/Now_THATS_a_Big_Dragon?utm_source=demo_version');
            }}
          >
            <img src="./ui/buttons/steam_pixel.png" alt="steam logo" />
            <span>{t('wishlistNow')}</span>
          </a>
        </div>
        <div>
          <a
            id="discord"
            className={styles.discordButton}
            onClick={() => {
              openLink('https://discord.gg/9XjNngDzPb');
            }}
          >
            <span>{t('joinNow')}</span> <img src="./ui/buttons/discord_pixel.png" alt="discord logo" />
          </a>
        </div>
      </div>
      <div className={styles.header}>
        <HealthBar />
      </div>
      <div className={`${styles.timedEventsContainer} ${!showUI ? styles.hideUI : ''}`}>
        <TimedEvents />
      </div>
      <div className={styles.gameArea}>
        <FloatingNumbersSource />
      </div>
      <div className={`${styles.generatorsContainer} ${!showUI ? styles.hideUI : ''}`}>
        <GeneratorsList />
      </div>
      <div className={styles.settingsContainer}>
        <GameButton
          id="toggle"
          labelId="buy_max"
          onClick={() => dispatch({ type: GUIActions.TOGGLE_BUY_MAX })}
          checked={buyMax}
          width={52}
          hotkey="b"
          data-testid="buy-max-toggle"
        />
        <SettingsMenu />
        <DungeonCrawler />
      </div>
      <div className={`${styles.statisticsContainer} ${!showUI ? styles.hideUI : ''}`}>
        <StatsAndInfoList />
      </div>

      {crtFilterEnabled && <CRTFilter />}
    </div>
  );
};
